import type { ProductConfigBuilder } from "../product-types";
import { DECO, breadcrumb } from "./shared";
import heroLifestyle from "@/assets/products/tv5/hero-lifestyle.png";
import ports from "@/assets/products/tv5/ports-closeup-clean.jpg";
import remote from "@/assets/products/tv5/remote-clean.jpg";
import softwareLifestyle from "@/assets/products/tv5/software-lifestyle.jpg";
import performanceOpaque from "@/assets/products/tv5-ultra/performance-opaque.jpg";
import performanceTransparent from "@/assets/products/tv5-ultra/performance-transparent.jpg";

export const buildTv5Config: ProductConfigBuilder = (ctx) => {
  const m = ctx.messages;
  const u = ctx.urls;

  return {
    breadcrumb: breadcrumb(ctx, "inext TV5"),
    sections: [
      {
        kind: "hero",
        bgClassName: "bg-muted/10",
        decorations: [{ kind: "grid", className: DECO.grid25 }],
        primaryGlow: false,
        eyebrow: m.tv5HeroEyebrow,
        headline: m.tv5HeroHeadline,
        sub: m.tv5HeroSub,
        contactHref: u.contactSales,
        contactLabel: m.tv6UltraContactSales,
        distributorHref: u.distributors,
        distributorLabel: m.tv6UltraFindDistributor,
        compareHref: u.compare,
        compareLabel: m.tv5CompareLabel,
        media: {
          type: "image",
          src: heroLifestyle,
          alt: m.tv5HeroImageAlt,
          width: 1200,
          height: 800,
        },
      },
      {
        kind: "glance",
        bgClassName: "bg-card/50",
        stats: [
          [m.tv5GlanceValue1, m.tv5GlanceLabel1],
          [m.tv5GlanceValue2, m.tv5GlanceLabel2],
          [m.tv5GlanceValue3, m.tv5GlanceLabel3],
          [m.tv5GlanceValue4, m.tv5GlanceLabel4],
        ],
      },
      {
        kind: "intro",
        name: "TV5",
        paras: [m.tv5IntroPara1, m.tv5IntroPara2],
      },
      {
        kind: "connectivity",
        decorations: [{ kind: "grid", className: DECO.grid20 }],
        layout: "imageLeft",
        eyebrow: m.tv6UltraV2ConnectivityEyebrow,
        title: m.tv5ConnectivityTitle,
        caption: { style: "list", value: m.tv5ConnectivityCaption },
        media: {
          type: "image",
          src: ports,
          alt: m.tv5PortsImageAlt,
          width: 1400,
          height: 933,
          loading: "lazy",
        },
      },
      {
        kind: "performance",
        bgClassName: "bg-muted/15",
        layout: "imageRight",
        eyebrow: m.tv6UltraV2PerformanceEyebrow,
        title: m.tv5PerformanceTitle,
        body: m.tv5PerformanceBody,
        accent: { style: "chips", value: m.tv5PerformanceAccent },
        media: {
          type: "reveal",
          opaque: performanceOpaque,
          transparent: performanceTransparent,
          width: 1200,
          height: 800,
          label: m.tv5HardwareRevealLabel,
          alt: m.tv5HardwareRevealImageAlt,
        },
      },
      {
        kind: "remote",
        decorations: [{ kind: "primaryBlob", className: DECO.remotePrimaryBlob }],
        layout: "imageLeft",
        eyebrow: m.tv6RemoteEyebrow,
        title: m.tv5RemoteTitle,
        body: m.tv5RemoteBody,
        bullets: [m.tv5RemoteFeature1, m.tv5RemoteFeature2, m.tv5RemoteFeature3],
        appLink: { href: u.remoteApp, label: m.remoteAppProductCallout },
        note: {
          title: m.tv5RemoteIrNoteTitle,
          body: m.tv5RemoteIrNoteBody,
          link: {
            href: `${u.accessories}?category=remotes#accessory-list`,
            label: m.tv6RemoteBtNoteLink,
          },
        },
        media: {
          type: "image",
          src: remote,
          alt: m.tv5RemoteImageAlt,
          width: 1000,
          height: 1000,
          loading: "lazy",
          maxWidth: true,
        },
      },
      {
        kind: "software",
        bgClassName: "bg-muted/20",
        layout: "imageRight",
        eyebrow: m.tv6UltraV2SoftwareEyebrow,
        title: m.tv5SoftwareTitle,
        body: m.tv5SoftwareBody,
        cards: [
          [m.tv6UltraV2SoftwareCustomTitle1, m.tv6UltraV2SoftwareCustomBody1],
          [m.tv6UltraV2SoftwareCustomTitle2, m.tv6UltraV2SoftwareCustomBody2],
        ],
        media: {
          type: "image",
          src: softwareLifestyle,
          alt: m.tv5SoftwareImageAlt,
          width: 1200,
          height: 675,
          loading: "lazy",
          shadowGlow: true,
        },
      },
      {
        kind: "alcatraz",
        pill: m.operationsStatPill,
        title: m.tv6UltraAlcatrazTitle,
        body: m.tv5AlcatrazBody,
        ctaHref: u.alcatraz,
        ctaLabel: m.tv6UltraAlcatrazCta,
      },
      {
        kind: "specs",
        bgClassName: "bg-muted/10",
        title: m.tv6UltraV2SpecsTitle,
        note: m.packageNote,
        groups: [
          {
            title: m.tv6UltraV2SpecsGroupHardware,
            rows: [
              [m.tv6UltraSpecChipset, "Allwinner H313"],
              [m.tv6UltraSpecCpu, m.productSpecCpuArmv8Quad],
              [m.tv6UltraSpecGpu, "ARM Mali-G31 MP2"],
              [m.tv6UltraSpecRam, "1 GB DDR3"],
              [m.tv6UltraSpecStorage, "8 GB eMMC"],
              [m.tv6UltraSpecDimensions, "10.5 × 10.5 × 2.0 cm"],
              [m.tv6UltraSpecPower, "5 V / 2 A"],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupConnectivity,
            rows: [
              [m.tv6UltraSpecHdmi, m.productSpecHdmiCableIncluded],
              [m.tv6UltraSpecEthernet, m.productSpecEthernet100],
              [m.tv6UltraSpecWifi, m.productSpecWifiDualBandN],
              [m.tv6UltraSpecUsb, m.productSpecUsbHost],
              [m.tv6UltraSpecCardReader, "microSD / TF"],
              [m.tv6UltraSpecAudioOut, m.productSpecAudioSpdifRcaTrs],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupMedia,
            rows: [
              [m.tv6UltraSpecMaxRes, m.tv6UltraSpecMaxResValue],
              [
                m.tv6UltraSpecVideoCodecs,
                "H.265 (4K@60) · VP9 P2 (4K@30) · H.264 (4K@30) · MPEG-1/2/4 · AVS · WMV9 · VP8 · Xvid/DivX",
              ],
              [
                m.tv6UltraSpecVideoFormats,
                "AVI · MKV · ISO · VOB · DAT · MPG/MPEG · MP4 · MOV · TS · XVID · ASF · FLV · WMV",
              ],
              [
                m.tv6UltraSpecAudioFormats,
                "MP3 · WMA · AAC · WAV · OGG · MIDI · FLAC · APE · ALAC · M4A",
              ],
              [m.tv6UltraSpecImageFormats, "JPG/JPEG · HD JPEG · TIFF · PNG · BMP · GIF"],
              [m.tv6UltraSpec3d, m.tv6UltraSpec3dValue],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupSoftware,
            rows: [
              [m.tv6UltraSpecOs, "Android 10 (AOSP)"],
              [m.tv6UltraSpecDms, "inext Alcatraz™ DMS"],
              [m.tv6UltraSpecTr069, m.tv6UltraSpecTr069Yes],
              [m.tv6UltraSpecFilesystems, "exFAT · FAT32 · NTFS · EXT · HFS+"],
              [m.tv6UltraSpecLanguages, m.tv6UltraSpecLanguagesValue],
              [m.tv6UltraSpecInternet, m.productSpecInternetFeatures],
            ],
          },
          {
            title: m.tv6UltraV2SpecsGroupPackage,
            rows: [
              ["·", m.tv6PackageRemote],
              ["·", m.tv6UltraPackagePower],
              ["·", m.tv6UltraPackageHdmi],
              ["·", m.tv6UltraPackageBatteries],
              ["·", m.tv6UltraPackageGuide],
            ],
          },
        ],
      },
      {
        kind: "cta",
        title: m.tv5CtaTitle,
        body: m.tv5CtaBody,
        compareHref: u.compare,
        compareLabel: m.tv5CompareLabel,
        contactHref: u.contactSales,
        contactLabel: m.tv6UltraContactSales,
        distributorHref: u.distributors,
        distributorLabel: m.tv6UltraFindDistributor,
      },
    ],
  };
};
